import React from 'react';
import { cn } from '@/lib/utils';

export interface Action {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** First action defaults to primary accent; others render as secondary surface buttons. */
  variant?: 'primary' | 'secondary';
}

export interface ActionBarProps {
  actions: Action[];
  className?: string;
}

/**
 * ActionBar renders page-level actions with one accent primary and secondary surface buttons.
 * On mobile, buttons stack vertically; on larger screens they display inline.
 */
export const ActionBar: React.FC<ActionBarProps> = ({ actions, className }) => {
  return (
    <div className={cn('flex flex-col sm:flex-row gap-3', className)}>
      {actions.map(({ label, onClick, icon, disabled, variant }, idx) => {
        const isPrimary = variant === 'primary' || (variant !== 'secondary' && idx === 0);
        return (
          <button
            key={idx}
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
              'inline-flex items-center justify-center px-4 py-2 h-12 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed',
              isPrimary
                ? 'bg-accent text-accent-fg hover:bg-accent/90 focus:ring-accent'
                : 'border border-line bg-surface text-fg hover:bg-surface/80 focus:ring-accent',
            )}
          >
            {icon && <span className="mr-2 h-5 w-5">{icon}</span>}
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default ActionBar;

